
import {FetchResult} from "@apollo/client";
import {
    CreateUploadRequestAssetType,
    CreateUploadRequestV2Mutation, ImageInput,
    useCreatePostMutation,
    useCreateUploadRequestV2Mutation,
} from "@/graphql/API";
import {Campaign, CollectUserData} from "@/models/campaign";
import {defineComponent, PropType} from "vue";
import {Contribution, totalContributionDuration} from "@/models/contribution";
import {Recorder} from "@/utils/recorder";
import {Recording} from "@/models/recording";
import {provideApolloClient} from "@vue/apollo-composable";
import {apolloClient} from "@/providers/graphql.provider";
import {UploadRequest, UploadRequestKind} from "@/models/upload-request";
import axios from "axios";
import InactiveComponent from "@/components/recorder/pages/InactiveComponent.vue";
import VideoComponent from "@/components/recorder/pages/VideoComponent.vue";
import CSATComponent from "@/components/recorder/pages/CSATComponent.vue";
import DetailsComponent from "@/components/recorder/pages/DetailsComponent.vue";
import PromptComponent from "@/components/recorder/pages/PromptComponent.vue";
import RecordComponent from "@/components/recorder/pages/RecordComponent.vue";
import SuccessComponent from "@/components/recorder/pages/SuccessComponent.vue";
import NPSComponent from "@/components/recorder/pages/NPSComponent.vue";
import {getContrast, getRgbColorFromHex, RGB} from "@/utils/dominantColorOptimiser";
import {Helper} from "@/utils/helper";
import {mapActions} from "pinia";
import {useAnalyticsStore} from "@/store/analytics.store";
import VideoMixin from "@/components/recorder/VideoMixin.vue";

const DEFAULT_FOREGROUND_COLOR = "#46DEB5";
const DEFAULT_BACKGROUND_COLOR = "#FFFFFF";

type PermissionState = "denied" | "granted" | "prompt" | 'unknownDueToOldBrowser';

interface RecorderViewData {
    isLoading: boolean;
    error?: string;
    contribution?: Contribution;
    state?: RecorderState;
    recorder?: Recorder;
    hasRecordingCapabilities?: boolean;
    microphonePermissionsState?: PermissionState;
    isWebsitePermissionDenied?: boolean;
    recordingRequested?: boolean;
    didMediaStreamCreationFail?: boolean;
    conversationRoundIndex: number;
}

export enum RecorderState {
    Inactive = 'inactive',
    Video = 'video',
    NPS = 'nps',
    CSAT = 'csat',
    Prompt = 'prompt',
    Record = 'record',
    Details = 'details',
    Success = 'success'
}

interface FileUploadConfig {
    protocol: string;
    method: string;
    headers: { [name: string]: string };
    url: string;
    fileName: string;
    provider: string;
    data: FormData | ArrayBuffer | Blob;
}

export default defineComponent( {
    name: "LegacyRecorderView",
    components: {
        VideoComponent,
        NPSComponent,
        DetailsComponent, RecordComponent, PromptComponent, CSATComponent, InactiveComponent, SuccessComponent
    },
    mixins: [VideoMixin],
    data(): RecorderViewData {
        return {
            isLoading: false,
            hasRecordingCapabilities: undefined,
            error: undefined,
            state: undefined,
            contribution: undefined,
            recorder: undefined,
            microphonePermissionsState: undefined,
            isWebsitePermissionDenied: undefined,
            recordingRequested: undefined,
            didMediaStreamCreationFail: undefined,
            conversationRoundIndex: 0,
        };
    },
    props: {
        campaign: {
            type: Object as PropType<Campaign>,
            required: true,
        },
        isPreview: {
            type: Boolean,
            required: false,
            default: false,
        },
        previewState: {
            type: String as PropType<string>,
            required: false,
        }
    },
    watch: {
        previewState() {
            if (this.isPreview && this.previewState !== undefined) {
                this.processCampaignForPreview(this.previewState);
            }
        },
        campaign() {
            this.setLanguage();
            this.setCSSVarCampaignColors();
        },
    },
    mounted() {
        this.setLanguage();
        this.setCSSVarCampaignColors();

        if (this.isPreview && this.previewState !== undefined) {
            this.processCampaignForPreview(this.previewState);
        } else {
            this.trackEvent("creationInitiated", {
                ...this.$analytics.campaignParams(this.campaign),
            });
            this.trackPageView(this.$route.query);

            this.processCampaign();
            this.prepareForRecording();
        }
    },
    computed: {
        inAppPresentation(): boolean {
            return typeof window.BeamsSDK !== 'undefined';
        },
        isInactive(): boolean {
            return this.state !== undefined && this.state === RecorderState.Inactive;
        },
        isVideo(): boolean {
            return this.state !== undefined && this.state === RecorderState.Video;
        },
        isCSAT(): boolean {
            return this.state !== undefined && this.state === RecorderState.CSAT;
        },
        isNPS(): boolean {
            return this.state !== undefined && this.state === RecorderState.NPS;
        },
        isPrompt(): boolean {
            return this.state !== undefined && this.state === RecorderState.Prompt;
        },
        isRecord(): boolean {
            return this.state !== undefined && this.state === RecorderState.Record;
        },
        isDetails(): boolean {
            return this.state !== undefined && this.state === RecorderState.Details;
        },
        isSuccess(): boolean {
            return this.state !== undefined && this.state === RecorderState.Success;
        },

        isRecordingPossible() {
            return this.hasRecordingCapabilities && this.campaign?.canAcceptResponses && !this.isWebsitePermissionDenied && !this.didMediaStreamCreationFail;
        },

        currentQuestion() {
            return this.contribution?.conversation[this.conversationRoundIndex].question || "";
        },
        recordingDeniedReason() {
            if (!this.campaign) {
                return null;
            }

            if (!this.campaign.canAcceptResponses) {
                return this.$t('creation.quotaReached');
            } else if (!this.isRecordingPossible) {
                return this.$t('creation.microphoneMissing');
            }
            return null;
        },

        collectingDataEnabled() {
            return this.campaign?.collectNames !== CollectUserData.Disabled || this.campaign?.collectEmails !== CollectUserData.Disabled || this.campaign?.collectImages !== CollectUserData.Disabled;
        },

        color(): string {
            if (this.campaign?.color) {
                return this.campaign.color;
            }

            if (this.campaign?.profile.brandColor) {
                return this.campaign.profile.brandColor;
            }

            return DEFAULT_FOREGROUND_COLOR;
        },

        backgroundColor(): string {
            if (this.campaign?.backgroundColor) {
                return this.campaign.backgroundColor;
            }

            return DEFAULT_BACKGROUND_COLOR;
        },
        primaryTextColor(): string {
            if (this.contrast(this.color, DEFAULT_BACKGROUND_COLOR) > 4.0) {
                return "#FFFFFF";
            }

            return "#000000";
        },
        textColorOnBackground(): string {
            if (this.contrast(this.backgroundColor, DEFAULT_BACKGROUND_COLOR) > 4.0) {
                return "#FFFFFF";
            }

            return "#000000";
        },
    },
    methods: {
        // analytics store
        ...mapActions(useAnalyticsStore, ['trackPageView']),
        ...mapActions(useAnalyticsStore, ['trackEvent']),

        sdkClose() {
            window.BeamsSDK?.close();
        },
        
        // STYLES
        setLanguage(): void {
            if (this.campaign.interfaceLanguage) {
                this.$i18n.locale = this.campaign.interfaceLanguage.split("-")[0].toLowerCase();
            }
        },
        contrast(primaryColor: string, secondaryColor: string): number {
            const primary: RGB = getRgbColorFromHex(primaryColor);
            const secondary: RGB = getRgbColorFromHex(secondaryColor);
            return getContrast(primary, secondary);
        },
        setCSSVarCampaignColors() {
            const root = document.documentElement;
            root.style.setProperty('--creation-primary-color', getRgbColorFromHex(this.color).join(' '));
            root.style.setProperty('--creation-bg-color', getRgbColorFromHex(this.backgroundColor).join(' '));
            root.style.setProperty('--creation-primary-text-color', getRgbColorFromHex(this.primaryTextColor).join(' '));
            root.style.setProperty('--creation-text-color', getRgbColorFromHex(this.textColorOnBackground).join(' '));

            if (this.contrast(this.backgroundColor, DEFAULT_BACKGROUND_COLOR) > 4.0) {
                // Dark Mode
                root.style.setProperty('--creation-opacity-full', '1');
                root.style.setProperty('--creation-opacity-high', '0.7');
                root.style.setProperty('--creation-opacity-medium', '0.5');
                root.style.setProperty('--creation-opacity-low', '0.3');
                root.style.setProperty('--creation-opacity-separator', '0.2');
                root.style.setProperty('--creation-opacity-circle1', '0.12');
                root.style.setProperty('--creation-opacity-circle2', '0.1');
            } else {
                // Light Mode
                root.style.setProperty('--creation-opacity-full', '1');
                root.style.setProperty('--creation-opacity-high', '0.6');
                root.style.setProperty('--creation-opacity-medium', '0.4');
                root.style.setProperty('--creation-opacity-low', '0.2');
                root.style.setProperty('--creation-opacity-separator', '0.1');
                root.style.setProperty('--creation-opacity-circle1', '0.05');
                root.style.setProperty('--creation-opacity-circle2', '0.03');
            }
        },

        async prepareForRecording(): Promise<void> {
            this.recorder = new Recorder();
            this.hasRecordingCapabilities = this.recorder.firstSupportedMediaType() !== undefined;
            this.microphonePermissionsState = await this.poolUntilMicrophoneStatusIsNotPrompt();
            this.determineNextState();
        },

        // Only Chrome browser currently allows us to subscribe to changes, so we need to pool
        // New API to listen to permission changes (onchange) is not working in Safari, so the
        // microphone state is never updated in the case of using it.
        // The solution now is to ask for the status until we get the answer (granted, denied)
        // and then act accordingly
        poolUntilMicrophoneStatusIsNotPrompt(): Promise<PermissionState> {
            return new Promise((resolve) => {
                const interval = setInterval(async () => {
                    if (window.navigator.permissions && window.navigator.permissions.query) {
                        try {
                            // eslint-disable-next-line no-undef
                            const permissionName = "microphone" as PermissionName;
                            const result = await window.navigator.permissions.query({ name: permissionName });
                            if (result.state !== RecorderState.Prompt) {
                                clearInterval(interval);
                                resolve(result.state);
                            }
                        } catch (e) {
                            console.error('Unable to inspect navigator permissions');
                            console.error(e);

                            // Firefox supports window.navigator.permissions, but not for microphone. So, querying will throw
                            // an error and we can safely assume we'l never know at this point
                            clearInterval(interval);
                            resolve('unknownDueToOldBrowser');
                        }
                    } else {
                        // older browser such as Safari iOS 15 don't support navigator permissions API. The only
                        // possible way to detect permission rejection is to try to create MediaStream and catch it fail.
                        clearInterval(interval);
                        resolve('unknownDueToOldBrowser');
                    }
                }, 100);
            });
        },

        determineNextState(): void {
            if (this.microphonePermissionsState === undefined) {
                return;
            }

            if (this.microphonePermissionsState === 'denied') {
                this.isWebsitePermissionDenied = true;
            } else if (this.recordingRequested && !this.didMediaStreamCreationFail && (this.microphonePermissionsState === 'granted' || this.microphonePermissionsState === 'unknownDueToOldBrowser')) {
                this.goToRecordingScreen();
                this.recordingRequested = false;
            }
        },

        async recordingRequestedEvent() {
            this.recordingRequested = true;

            try {
                await this.recorder?.createNewStream();
            } catch (e) {
                console.error("MediaStream can't be created");
                console.error(e);
                // An error at this point can mean:
                // a) browser itself, or the app hosting the in-app browser (such as LinkedIn), doesn't
                //    even have permissions to prompt the user. In such case the permissions state will stay
                //    'prompt' forever, but the stream will fail to be created. User never sees  the prompt.
                // b) older browsers, such as Safari iOS 15, don't have the permissions API, so the only way to
                //    learn about permission rejected is by an error throw in stream cration here
                this.didMediaStreamCreationFail = true;
            }

            this.determineNextState();
        },

        extractHref(): string | undefined {
            const href = this.$route.params.href;
            if (typeof href === 'string') {
                return href;
            } else if (Array.isArray(href)) {
                return href[0];
            } else {
                return undefined;
            }
        },

        processCampaign(): void {
            if (this.campaign === undefined) {
                return;
            }

            if (this.campaign.status !== 'active') {
                this.state = RecorderState.Inactive;
                return;
            }

            if (this.hasVideoURLs) {
                this.state = RecorderState.Video;
            } else if (this.campaign.isNPSCampaign) {
                this.state = RecorderState.NPS;
            } else if (this.campaign.isCSATCampaign) {
                this.state = RecorderState.CSAT;
            } else {
                this.state = RecorderState.Prompt;
            }

            this.contribution = {
                campaignID: this.campaign.id,
                recordings: [],
                externalID: this.$route.query.externalID as string | undefined,
                conversation: [
                    { question: this.campaign.promptText }
                ],
            };

            if (this.$route.query.name) {
                this.contribution.name = this.$route.query.name as string;
            }

            if (this.$route.query.email) {
                this.contribution.email = this.$route.query.email as string;
            }
        },

        goBackFromRecorder(): void {
            this.goToPromptScreen();
        },

        goToPromptScreen(): void {
            if (!this.contribution) {
                throw new Error("contribution expected")
            }

            this.state = RecorderState.Prompt;
            this.trackEvent('creationBackToPrompt', {
                ...this.$analytics.campaignParams(this.campaign),
                duration: totalContributionDuration(this.contribution),
            });
        },

        goToRecordingScreen(): void {
            if (!this.contribution) {
                throw new Error("contribution expected")
            }

            this.state = RecorderState.Record;
            this.trackEvent('creationRecordingOpened', {
                ...this.$analytics.campaignParams(this.campaign),
                duration: totalContributionDuration(this.contribution),
            });
        },

        goBack() {
            if (this.hasVideoURLs) {
                this.state = RecorderState.Video;
            } else if (this.campaign?.isNPSCampaign) {
                this.state = RecorderState.NPS;
            } else if (this.campaign?.isCSATCampaign) {
                this.state = RecorderState.CSAT;
            }
        },

        goToNextScreenFromVideoPreRoll(): void {
            if (this.campaign?.isNPSCampaign) {
                this.state = RecorderState.NPS;
            } else if (this.campaign?.isCSATCampaign) {
                this.state = RecorderState.CSAT;
            } else {
                this.state = RecorderState.Prompt;
            }
        },
        async goNextFromRecorder(): Promise<void> {
            if (!this.contribution) {
                throw new Error("contribution expected")
            }

            if (!this.collectingDataEnabled && this.contribution.recordings.length > 0  && !this.campaign?.enforcesTermsAndConditionsConsent) {
                await this.tryToSubmit();
            } else {
                this.state = RecorderState.Details;

                this.trackEvent('creationDetailsOpened', {
                    ...this.$analytics.campaignParams(this.campaign),
                    duration: totalContributionDuration(this.contribution),
                });
            }
        },

        async tryToSubmit(): Promise<void> {
            try {
                if (!this.contribution) {
                    return;
                }

                this.trackEvent('creationSubmitted', {
                    ...this.$analytics.campaignParams(this.campaign),
                    duration: totalContributionDuration(this.contribution),
                    hasImage: !!this.contribution.imageData,
                });

                this.isLoading = true;
                this.storeDataInLocalStorage();

                provideApolloClient(apolloClient);

                const [audioUploadResponse, imageUploadResponse] = await Promise.all([
                    this.uploadAudio(),
                    this.uploadImage(),
                ]);

                if (!audioUploadResponse) {
                    throw new Error('Audio upload failed');
                }

                let image: ImageInput | null = null;
                if (imageUploadResponse) {
                    image = { name: imageUploadResponse.fileName };
                }

                const { mutate } = useCreatePostMutation({
                    variables: {
                        audio: {
                            name: audioUploadResponse.fileName,
                            provider: audioUploadResponse.provider,
                        },
                        image: image,
                        campaignID: this.contribution.campaignID,
                        email: this.contribution.email,
                        name: this.contribution.name,
                        externalID: this.contribution.externalID,
                        rating: this.contribution.rating,
                    },
                });

                await mutate();

                this.state = RecorderState.Success;
                this.sendMessageToEmbeddingParent();
                this.trackEvent('creationCompleted', {
                    ...this.$analytics.campaignParams(this.campaign),
                    duration: totalContributionDuration(this.contribution),
                    hasImage: !!this.contribution.imageData,
                });
                // eslint-disable-next-line
            } catch (e: any) {
                console.error(e);
                this.trackEvent('creationErrored', {
                    ...this.$analytics.campaignParams(this.campaign),
                    error: e,
                });

                if (e && e.error && typeof e.error === 'function') {
                    this.error = e.error();
                }
            } finally {
                this.isLoading = false;
            }
        },

        recorderDataUpdated({recording, conversationRoundIndex, transcript }: { recording: Recording | undefined, transcript: string, conversationRoundIndex: number }): void {
            console.log('recorderDataUpdated', {recording, conversationRoundIndex, transcript })
            if (this.contribution === undefined) {
                return;
            }

            console.log(conversationRoundIndex, recording)

            if (recording) {
                this.contribution.recordings[conversationRoundIndex] = recording;
            } else {
                delete this.contribution.recordings[conversationRoundIndex];
            }
            this.contribution.conversation[conversationRoundIndex] = {
                ...this.contribution.conversation[conversationRoundIndex],
                transcript: transcript
            }
        },

        setContributionRating(rating: number): void {
            if (this.contribution === undefined) {
                return;
            }

            this.contribution.rating = rating;
        },

        setImageURL(imageURL: string) {
            if (this.contribution === undefined) {
                return;
            }
            this.contribution.imageURL = imageURL;
        },

        setImageData(imageData: ArrayBuffer) {
            if (this.contribution === undefined) {
                return;
            }
            this.contribution.imageData = imageData;
        },

        setName(name: string) {
            if (this.contribution === undefined) {
                return;
            }
            this.contribution.name = name;
        },

        setEmail(email: string) {
            if (this.contribution === undefined) {
                return;
            }
            this.contribution.email = email;
        },

        sendMessageToEmbeddingParent(): void {
            if (!window.Cypress) {
                Helper.sendMessageToParentWindow({ type: 'Beams::CreationCompleted' });
            }
        },

        async uploadImage(): Promise<FileUploadConfig | void> {
            if (this.contribution?.imageData === undefined) {
                return;
            }

            return this.upload(this.contribution.imageData, CreateUploadRequestAssetType.Image);
        },

        async uploadAudio(): Promise<FileUploadConfig | void> {
            if (!this.contribution) {
                throw new Error("Contribution expected")
            }

            if (this.contribution.recordings.length < 1) {
                return;
            }
            const mergedBlob = await this.mergeBlobs(this.contribution.recordings.map((recording: Recording) => recording.blob));
            return this.upload(mergedBlob, CreateUploadRequestAssetType.Audio);
        },

        async mergeBlobs(blobs: Blob[]) {
            const arrayBuffers = await Promise.all(blobs.map(blob => blob.arrayBuffer()));
            const combinedArray = new Uint8Array(
                arrayBuffers.reduce((acc, buffer) => acc + buffer.byteLength, 0)
            );
            let offset = 0;
            for (const buffer of arrayBuffers) {
                combinedArray.set(new Uint8Array(buffer), offset);
                offset += buffer.byteLength;
            }
            return new Blob([combinedArray]);
        },

        prepareFileUploadConfig(uploadRequestResponse: UploadRequest, data: ArrayBuffer | Blob): FileUploadConfig {
            let dataForUpload: FormData | ArrayBuffer | Blob = data;
            const headers = uploadRequestResponse.headers.reduce((headers: { [name: string]: string }, header) => {
                headers[header.key] = header.value;
                return headers;
            }, {});

            if (uploadRequestResponse.kind === UploadRequestKind.MultipartForm) {
                const form = new FormData();
                form.append(uploadRequestResponse.fileParameterName, new Blob([data]), uploadRequestResponse.fileName);

                uploadRequestResponse.parameters.forEach((parameter) => {
                    form.append(parameter.key, parameter.value);
                });

                headers['Content-Type'] = 'multipart/form-data';
                dataForUpload = form;
            }

            return {
                protocol: 'https',
                method: uploadRequestResponse.httpMethod,
                headers: headers,
                url: uploadRequestResponse.url,
                fileName: uploadRequestResponse.fileName,
                provider: uploadRequestResponse.provider,
                data: dataForUpload,
            };
        },

        async upload(data: ArrayBuffer | Blob, type: CreateUploadRequestAssetType): Promise<FileUploadConfig | void> {
            const { mutate } = useCreateUploadRequestV2Mutation({
                variables: {
                    assetType: type
                },
            });

            const mutationResponse: FetchResult<CreateUploadRequestV2Mutation> | null = await mutate();
            if (!mutationResponse) {
                return;
            }

            const uploadRequest = mutationResponse.data?.createUploadRequest as UploadRequest;
            const uploadConfig = this.prepareFileUploadConfig(uploadRequest, data);
            await axios(uploadConfig);
            return uploadConfig;
        },

        reset(): void {
            this.processCampaign();
            this.isLoading = false;
            this.error = undefined;
            this.recordingRequested = false;
        },

        storeDataInLocalStorage(): void {
            if (this.contribution?.email) {
                Helper.setLocalStorageItem('contributor-email', this.contribution.email);
            }
            if (this.contribution?.name) {
                Helper.setLocalStorageItem('contributor-name', this.contribution.name);
            }
        },

        processCampaignForPreview(state: string): void {
            this.hasRecordingCapabilities = true;
            this.microphonePermissionsState = "granted";
            this.contribution = {
                campaignID: 'preview-campaign-id',
                recordings: [],
                conversation: [],
            }
            switch (state) {
                case 'success':
                    this.state = RecorderState.Success;
                    break;
                case 'nps':
                    this.state = RecorderState.NPS;
                    break;
                case 'csat':
                    this.state = RecorderState.CSAT;
                    break;
                case 'details':
                    this.state = RecorderState.Details;
                    break;
                case 'video':
                    this.state = RecorderState.Video;
                    break;
                default:
                    this.state = RecorderState.Prompt;
            }
        }
    },
});
